import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./ambiente";

/* ============================================================
   Calorias por IA

   Só chamadas: a estimativa acontece inteira no servidor porque a chave
   da IA não pode chegar no navegador, e porque a foto sai do Storage
   direto para a API sem passar pelo aparelho de ninguém.
   ============================================================ */

export interface ItemDeCaloria { nome: string; porcao: string; kcal: number }

export interface Estimativa {
  ok: boolean;
  fonte?: "ia" | "base";
  motivo?: string;
  detalhe?: string;
  kcal?: number;
  kcalMin?: number;
  kcalMax?: number;
  itens?: ItemDeCaloria[];
  confianca?: "alta" | "media" | "baixa";
  observacao?: string;
  /** Quantas vezes o grupo já viu este prato, quando veio da base. */
  vezes?: number;
}

/* O prefixo `nhomi` no nome publicado não é enfeite: o GymEats vive no
   mesmo projeto e na mesma região, e nome de Cloud Function é único aí.
   Sem o prefixo, publicar um lado apaga a função do outro. Ver o
   comentário em functions/src/index.ts. */

export async function estimarCalorias(cid: string, pid: string): Promise<Estimativa> {
  const chamar = httpsCallable<{ cid: string; pid: string }, Estimativa>(
    functions, "nhomiEstimarCalorias");
  const { data } = await chamar({ cid, pid });
  return data;
}

export async function corrigirCalorias(
  cid: string, pid: string, kcal: number,
): Promise<void> {
  const chamar = httpsCallable<{ cid: string; pid: string; kcal: number }, unknown>(
    functions, "nhomiCorrigirCalorias");
  await chamar({ cid, pid, kcal });
}

/** O que cada `kcalSource` quer dizer, em português. */
export const FONTE: Record<string, string> = {
  ai: "estimado por IA",
  base: "da base do grupo",
  manual: "confirmado por quem postou",
};

export const formatKcal = (n: number | null | undefined): string =>
  n == null ? "—" : `${Math.round(n).toLocaleString("pt-BR")} kcal`;

/* ---------- quanto ainda cabe hoje ---------- */

/**
 * O dia como o SERVIDOR conta.
 *
 * `America/Sao_Paulo` explícito, e não o fuso do aparelho: o contador em
 * `users/{uid}/usage/{dia}` é escrito pela função, que usa São Paulo.
 * Quem abrisse o app viajando leria o documento de outro dia e veria uma
 * cota cheia que o servidor não vai honrar.
 */
const diaDoServidor = (): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());

/**
 * Quantas estimativas esta pessoa já pediu hoje.
 *
 * A regra deixa cada um ler o próprio contador — e é isso que permite a
 * tela dizer "restam 3 de 5" ANTES de a pessoa apertar. Avisar antes é o
 * que separa um limite de produto de um erro na cara.
 *
 * Isto é para MOSTRAR. Quem recusa a estimativa acima do teto é a função,
 * que confere o mesmo contador numa transação.
 */
export function watchUsoDeHoje(uid: string, cb: (usadas: number) => void): () => void {
  return onSnapshot(
    doc(db, "users", uid, "usage", diaDoServidor()),
    (s) => cb(Number(s.data()?.["estimativas"] ?? 0)),
    () => cb(0),
  );
}

/**
 * A estimativa falhou porque a COTA DESTA PESSOA acabou?
 *
 * Duas coisas diferentes chegam como `resource-exhausted`: o teto diário
 * de quem pediu, que só volta na virada do dia, e a cota da IA que é
 * nossa e passa em minutos. A tela precisa saber qual — a primeira troca
 * o botão por uma explicação e uma porta pro Pro; a segunda é um "tenta
 * daqui a pouco" e o botão fica.
 *
 * Quem distingue é o `motivo` que a função manda nos detalhes. Ler a
 * FRASE funcionaria hoje e quebraria calado no dia em que alguém
 * melhorar o texto.
 */
export const cotaDaPessoaAcabou = (err: unknown): boolean =>
  (err as { details?: { motivo?: unknown } } | null)?.details?.motivo === "cota-da-pessoa";

/**
 * Tira o `[429]` que o SDK do Firebase gruda no fim da mensagem.
 *
 * Código HTTP não diz nada para quem está com fome olhando a foto de um
 * ovo, e a mensagem do servidor já explica o que houve em português.
 */
export const limparRecado = (texto: string): string =>
  texto.replace(/\s*\[\d{3}\]\s*$/, "").trim();
